import cn from 'classnames';
import OpProductPreview from 'components/Common/op-ui-wrappers/OpProductPreview/OpProductPreview';
import OpText from 'components/Common/op-ui-wrappers/OpText/OpText';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import getCurrentPreviewMask from 'store/search/selector/getCurrentPreviewMask';
import { PreviewMask } from 'store/search/types';
import gtmSendEventAsync from 'utils/gtm/gtmSendEventAsync';
import OpButton from 'components/Common/op-ui-wrappers/OpButton/OpButton';
import { HighlightItem } from 'store/favourite/types';
import isMobile from 'store/app/selector/isMobile';
import { setAddingProductToBasketAction } from 'store/app/slice';
import styles from './ProductItem.module.scss';

export interface OwnProps {
  product: HighlightItem;
  index: number;
}

export const reorderItemRedirect = (encDesignId: string) => {
  const targetUrl = encDesignId ? `/my-cart?action=add_design_to_cart&design_id=${encDesignId}` : `/my-orders`;
  document.location.href = targetUrl;
};

const RegularPreview = ({ images }: { images: string[] }) => {
  const [main, secondary] = images;
  return (
    <>
      <img
        className={cn([styles.preview, { [styles.hasSecondaryPreview]: !!secondary }])}
        src={main}
        loading="lazy"
        alt=""
      />
      {secondary && <img className={styles.secondaryPreview} src={secondary} loading="lazy" alt="" />}
    </>
  );
};

const MaskedPreview = ({ image, mask }: { image: string; mask: PreviewMask }) => {
  return (
    <>
      <OpProductPreview
        className={cn(styles.preview, styles.hasSecondaryPreview)}
        contentSrc={image}
        backgroundSrc={mask.images[0].background}
        foregroundSrc={mask.images[0].foregroundMask}
        maskPosition={mask.imageCoordinates}
      />
      <img className={styles.secondaryPreview} src={image} loading="lazy" alt="" />
    </>
  );
};

const ProductItemContent = ({ product }: Omit<OwnProps, 'index'>) => {
  const { t } = useTranslation();
  const mask = useSelector(getCurrentPreviewMask);
  const isMobileDevice = useSelector(isMobile);
  const dispatch = useDispatch();

  const reorderItem = async () => {
    dispatch(setAddingProductToBasketAction(true));
    await gtmSendEventAsync({
      event: 'reorder-clicked',
      eventCategory: 'memory-lane',
      eventAction: 'reorder',
      productTypeName: product.productTier,
      productTypeId: product.productTypeId,
    });
    setTimeout(() => {
      reorderItemRedirect((product as any).encDesignId);
    }, 300);
  };

  const titleVariant = isMobileDevice ? 'label-medium-light' : 'body-medium-light';
  const purchasedVariant = isMobileDevice ? 'body-small-regular' : 'body-large-regular';
  const reorderVariant = isMobileDevice ? 'small' : 'medium';
  return (
    <div className={styles.productItem}>
      <div className={styles.productImageWrapper}>
        <div className={styles.productImage} onClick={reorderItem}>
          {mask && product.isExternalAsset ? (
            <MaskedPreview image={product.images[0] || ''} mask={mask} />
          ) : (
            <RegularPreview images={[...product.images, product.secondaryImage]} />
          )}
        </div>
      </div>
      <div className={styles.productDescription}>
        <OpText block variant={titleVariant} className={styles.categoryName} color="natural-70">
          {product.productTier}
        </OpText>
        <OpText block variant={purchasedVariant} className={styles.productName}>
          {t('txt_purchased_on')} <span>{product.purchaseDate}</span>
        </OpText>
        <div className={styles.buttonContainer}>
          <OpButton onClick={reorderItem} size={reorderVariant} variant="secondary">
            {t('txt_reorder')}
          </OpButton>
        </div>
      </div>
    </div>
  );
};

const ProductItem = ({ product }: OwnProps) => {
  return (
    <div className={cn(styles.productContainer, 'product')}>
      <ProductItemContent product={product} />
    </div>
  );
};

export default ProductItem;
